import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Youtube, Twitter, Facebook, MapPin } from 'lucide-react';
import { OFFICES, CONTACTS } from '../data/content';

const SOCIALS = [
  { label: 'Instagram', href: 'https://www.instagram.com/marketingmind.in/', Icon: Instagram, testId: 'footer-social-instagram' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/marketingmindin/', Icon: Linkedin, testId: 'footer-social-linkedin' },
  { label: 'X', href: 'https://x.com/MarketingMind_?lang=en', Icon: Twitter, testId: 'footer-social-twitter' },
  { label: 'Facebook', href: 'https://www.facebook.com/MarketingMind.in/', Icon: Facebook, testId: 'footer-social-facebook' },
  { label: 'YouTube', href: 'https://www.youtube.com/channel/UCLQpLoutS4sdso20nLQnuiA', Icon: Youtube, testId: 'footer-social-youtube' },
];

const office = OFFICES[0];

const Footer = () => (
  <footer data-testid="site-footer" className="relative z-10 mt-10 border-t border-[#C9A56C]/30">
    <div className="content-container pt-12 pb-7">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
        {/* Brand */}
        <div className="md:col-span-3">
          <div className="inline-flex items-center justify-center bg-[#4E3B2A] border border-[#C9A56C]/30 px-5 py-3 mb-5">
            <img
              src="/adaura-logo-trim.png"
              alt="AdAura Awards 2026"
              width="200"
              height="80"
              loading="lazy"
              decoding="async"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </div>
          <div className="flex flex-col items-start gap-2">
            <span className="text-[10px] tracking-[0.28em] uppercase text-[#C9A56C]/70">Presented By</span>
            <img
              src="/brand/marketing-mind-logo-trim.png"
              alt="Marketing Mind — India's leading marketing publication, presenter of the AdAura Awards"
              width="160"
              height="48"
              loading="lazy"
              decoding="async"
              className="h-8 md:h-10 w-auto object-contain"
              data-testid="footer-marketing-mind-logo"
            />
          </div>
        </div>

        {/* Connect */}
        <div className="md:col-span-3">
          <div className="eyebrow mb-3">Get In Touch</div>
          <div className="space-y-3">
            {CONTACTS.map((c) => (
              <div key={c.for} data-testid={`footer-contact-${c.for.replace(/\W+/g, '-').toLowerCase()}`}>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#C9A56C]/80 leading-tight">{c.for}</div>
                <a href={`mailto:${c.email}`} className="block text-white/85 hover:text-[#C9A56C] transition-colors text-sm break-all mt-1">{c.email}</a>
              </div>
            ))}
            <a href={`tel:${CONTACTS[0].phone.replace(/\s+/g, '')}`} className="block text-white/80 hover:text-[#C9A56C] transition-colors text-sm pt-1">{CONTACTS[0].phone}</a>
          </div>
        </div>

        {/* Office */}
        {office && (
          <div className="md:col-span-2" data-testid="footer-office">
            <div className="eyebrow mb-3">Office</div>
            <div className="flex items-start gap-2.5">
              <MapPin size={14} className="text-[#C9A56C] mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-white text-[12.5px] font-medium leading-snug">{office.label}</div>
                <div className="text-white/65 text-[12.5px] leading-[1.65] mt-1.5">{office.address}</div>
              </div>
            </div>
          </div>
        )}

        {/* Navigate */}
        <div className="md:col-span-2">
          <div className="eyebrow mb-3">Navigate</div>
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/about" className="text-white/75 hover:text-[#C9A56C]">About</Link>
            <Link to="/categories" className="text-white/75 hover:text-[#C9A56C]">Categories</Link>
            <Link to="/judging" className="text-white/75 hover:text-[#C9A56C]">Judging</Link>
            <Link to="/jury" className="text-white/75 hover:text-[#C9A56C]">Jury</Link>
            <Link to="/eligibility" className="text-white/75 hover:text-[#C9A56C]">Eligibility</Link>
            <Link to="/winners" className="text-white/75 hover:text-[#C9A56C]" data-testid="footer-link-winners">Winners</Link>
            <Link to="/gallery" className="text-white/75 hover:text-[#C9A56C]" data-testid="footer-link-gallery">Photo Gallery</Link>
            <Link to="/contact" className="text-white/75 hover:text-[#C9A56C]">Contact</Link>
          </div>
        </div>

        {/* Follow */}
        <div className="md:col-span-2">
          <div className="eyebrow mb-3">Follow</div>
          <div className="flex flex-wrap gap-3">
            {SOCIALS.map(({ label, href, Icon, testId }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={testId}
                className="w-9 h-9 border border-[#C9A56C]/40 flex items-center justify-center text-[#C9A56C] hover:bg-[#C9A56C] hover:text-black transition-colors"
              >
                <Icon size={14} />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10 pt-5 border-t border-[#C9A56C]/20 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[11px] uppercase tracking-[0.25em] text-white/40">
        <div>© 2026 AdAura Awards by Marketing Mind</div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-[#C9A56C] transition-colors">Privacy</Link>
          <Link to="/terms" data-testid="footer-link-terms" className="hover:text-[#C9A56C] transition-colors">Terms</Link>
          <Link to="/refund" data-testid="footer-link-refund" className="hover:text-[#C9A56C] transition-colors">Refund</Link>
          <Link to="/payment-terms" data-testid="footer-link-payment-terms" className="hover:text-[#C9A56C] transition-colors">Payment</Link>
          <Link to="/ip-policy" data-testid="footer-link-ip-policy" className="hover:text-[#C9A56C] transition-colors">IP Policy</Link>
          <Link to="/cookie-policy" data-testid="footer-link-cookie-policy" className="hover:text-[#C9A56C] transition-colors">Cookies</Link>
          <Link to="/disclaimer" data-testid="footer-link-disclaimer" className="hover:text-[#C9A56C] transition-colors">Disclaimer</Link>
          <Link to="/juror-code-of-conduct" data-testid="footer-link-juror-code" className="hover:text-[#C9A56C] transition-colors">Jury Charter</Link>
          <Link to="/entrant-consent" data-testid="footer-link-entrant-consent" className="hover:text-[#C9A56C] transition-colors">Entrant Consent</Link>
          <span>Crafted in India</span>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
